import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * Loadout cache — batches per-username lookups into a single request every
 * 40ms so chat scrollback (which references many senders) doesn't fire
 * one HTTP call per message.
 *
 * Usage:
 *   const loadout = useLoadout(username);
 *   loadout?.chat_color?.value  // → "#CCFF00" (or a CSS gradient string)
 *   loadout?.background?.value  // → CSS background-image
 *   loadout?.border?.value      // → color / gradient for avatar ring
 *   loadout?.avatar_effect?.value // → box-shadow (glow) or animation key
 */

const LoadoutContext = createContext(null);
const BATCH_DELAY_MS = 40;

export function LoadoutProvider({ children }) {
  const cacheRef = useRef(new Map());           // username -> loadout|null
  const listenersRef = useRef(new Map());       // username -> Set<setState>
  const pendingRef = useRef(new Set());         // usernames queued for next flush
  const timerRef = useRef(null);

  const notify = (uname, val) => {
    const set = listenersRef.current.get(uname);
    if (set) set.forEach(fn => fn(val));
  };

  const flush = useCallback(async () => {
    timerRef.current = null;
    const usernames = Array.from(pendingRef.current);
    pendingRef.current = new Set();
    if (!usernames.length) return;
    try {
      const r = await api.post("/shop/loadouts", { usernames });
      const data = r.data || {};
      for (const uname of usernames) {
        const val = data[uname] || null;
        cacheRef.current.set(uname, val);
        notify(uname, val);
      }
    } catch {
      // On failure, cache `null` so we don't retry forever. If the user
      // later equips a loadout, they can refresh to pick it up.
      for (const uname of usernames) {
        if (!cacheRef.current.has(uname)) cacheRef.current.set(uname, null);
        notify(uname, cacheRef.current.get(uname));
      }
    }
  }, []);

  const request = useCallback((uname) => {
    if (!uname) return;
    if (cacheRef.current.has(uname)) return;
    pendingRef.current.add(uname);
    if (!timerRef.current) timerRef.current = setTimeout(flush, BATCH_DELAY_MS);
  }, [flush]);

  const subscribe = useCallback((uname, cb) => {
    if (!uname) return () => {};
    if (!listenersRef.current.has(uname)) listenersRef.current.set(uname, new Set());
    listenersRef.current.get(uname).add(cb);
    return () => {
      const set = listenersRef.current.get(uname);
      if (set) {
        set.delete(cb);
        if (set.size === 0) listenersRef.current.delete(uname);
      }
    };
  }, []);

  const invalidate = useCallback((uname) => {
    if (!uname) return;
    cacheRef.current.delete(uname);
    pendingRef.current.add(uname);
    if (!timerRef.current) timerRef.current = setTimeout(flush, BATCH_DELAY_MS);
  }, [flush]);

  const value = { cacheRef, request, subscribe, invalidate };
  return <LoadoutContext.Provider value={value}>{children}</LoadoutContext.Provider>;
}

/**
 * Hook — returns the loadout for one username, undefined while loading.
 * Batches automatically with other calls in the same tick.
 */
export function useLoadout(username) {
  const ctx = useContext(LoadoutContext);
  const [val, setVal] = useState(() => ctx?.cacheRef.current.get(username));

  useEffect(() => {
    if (!ctx || !username) return undefined;
    // If already cached, sync state
    if (ctx.cacheRef.current.has(username)) {
      setVal(ctx.cacheRef.current.get(username));
    } else {
      ctx.request(username);
    }
    return ctx.subscribe(username, setVal);
  }, [ctx, username]);

  return val;
}

/** Force refresh of a user's loadout (e.g., after they equip a new item). */
export function useInvalidateLoadout() {
  const ctx = useContext(LoadoutContext);
  return ctx?.invalidate || (() => {});
}
